import { useCallback, useEffect, useMemo, useState } from 'react';
import { faChevronLeft, faChevronRight } from '@fortawesome/pro-regular-svg-icons';
import { useFieldTheme } from 'framework/field/hooks/useFieldTheme';
import { Icon } from 'framework/Icon';
import moment from 'moment';
import { parseDate } from 'utils/dates';

import { EMPTY_ARRAY, EMPTY_FUNCTION } from '../../constants';

import { CalendarContext } from './context/CalendarContext';
import { CalendarDays } from './days/CalendarDays';
import { useCalendarTheme } from './hooks/useCalendarTheme';
import { DAYS } from './constants';
import { CalendarIconElement } from './elements';
import { CALENDAR_MODE } from './enums';
import { CalendarProps } from './types';

import cn from './Calendar.module.scss';

export const Calendar = ({
  value = EMPTY_ARRAY,
  mode = CALENDAR_MODE.SINGLE,
  format = 'MM/DD/YYYY',
  onSelect = EMPTY_FUNCTION,
  min_date,
  max_date,
  theme,
}: CalendarProps) => {
  const [month, setMonth] = useState<moment.Moment>(moment());
  const [dates, setDates] = useState<moment.Moment[]>([]);
  const [update, setUpdate] = useState<boolean>(false);

  const getMinDate = useCallback((dates: string[]): moment.Moment => {
    if (dates.length === 0) return moment();

    const timestamps = dates.map((date) => moment(date).valueOf());
    const timestamp = Math.min(...timestamps);
    return moment(timestamp);
  }, []);

  useEffect(() => {
    const values = parseDate(value);
    const moments = values.map((date) => moment(date));
    setDates(moments);
    if (!update) {
      setMonth(getMinDate(values).startOf('month'));
    }
  }, [getMinDate, update, value]);

  const onClickDay = useCallback(
    (day: number, offset = 0) => {
      const date = month.clone().add(offset, 'months').date(day);
      setUpdate(true);

      switch (mode) {
        case CALENDAR_MODE.SINGLE:
          onSelect([date.format(format)]);
          break;
        case CALENDAR_MODE.RANGE: {
          if (dates.length === 0 || dates.length === 2) {
            onSelect([date.format(format)]);
          } else {
            const [start] = dates;
            const dates_sorted = [start, date].sort((a, b) => a.valueOf() - b.valueOf());
            const dates_formatted = dates_sorted.map((d) => d.format(format));
            onSelect(dates_formatted);
          }
          break;
        }
        case CALENDAR_MODE.MULTIPLE: {
          const is_selected = dates.some((d) => d.isSame(date, 'day'));
          if (is_selected) {
            const dates_filtered = dates.filter((d) => !d.isSame(date, 'day'));
            const dates_formatted = dates_filtered.map((d) => d.format(format));
            onSelect(dates_formatted);
          } else {
            const dates_sorted = [...dates, date].sort((a, b) => a.valueOf() - b.valueOf());
            const dates_formatted = dates_sorted.map((d) => d.format(format));
            onSelect(dates_formatted);
          }
        }
      }
    },
    [dates, format, month, mode, onSelect]
  );

  const onClickMonth = useCallback(
    (offset: number) => setMonth(month.clone().add(offset, 'month').startOf('month')),
    [month]
  );

  const context = useMemo(
    () => ({
      month,
      dates,
      theme,
      mode,
      min_date,
      max_date,
      onSelect,
      onClickDay,
      onClickMonth,
    }),
    [month, dates, min_date, max_date, theme, mode, onSelect, onClickDay, onClickMonth]
  );

  const field_theme = useFieldTheme(theme?.field);
  const calendar_theme = useCalendarTheme(theme?.calendar);

  return (
    <div className={cn.options}>
      <CalendarContext.Provider value={context}>
        <div className={cn.header}>
          <CalendarIconElement
            className={cn.icon}
            onClick={() => onClickMonth(-1)}
            theme={{ field: field_theme, calendar: calendar_theme }}
          >
            <Icon icon={faChevronLeft} width="30px" />
          </CalendarIconElement>
          <p>{month.format('MMMM YYYY')}</p>
          <CalendarIconElement
            className={cn.icon}
            onClick={() => onClickMonth(1)}
            theme={{ field: field_theme, calendar: calendar_theme }}
          >
            <Icon icon={faChevronRight} width="30px" />
          </CalendarIconElement>
        </div>
        <div className={cn.calendar}>
          <div className={cn.grid}>
            {DAYS.map((day, index) => (
              <div className={cn.day} key={`${day}-${index}`}>
                {day}
              </div>
            ))}
          </div>
          <div className={cn.grid}>
            <CalendarDays />
          </div>
        </div>
      </CalendarContext.Provider>
    </div>
  );
};
